import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Loader2, Folder, FileText, CheckCircle2, AlertCircle, ArrowRight, ShieldAlert, PlusSquare, X, Users, Trash2, Download, ExternalLink, XCircle, Ban, Clock, Copy, PlayCircle, IndianRupee, Info, ChevronDown, Send, Mail, Search, Building, Filter, FileCheck, TrendingUp } from 'lucide-react';
import * as XLSX from 'xlsx';
import { IdeasFilterTable } from '../components/IdeasFilterTable.tsx';
import { AmountDisplay } from '../components/AmountDisplay.tsx';
import { CountCardHint } from '../components/CountCardHint.tsx';
import { formatIndianCurrency } from '../utils/currency.ts';
import { useSessionState } from '../hooks/useSessionState.ts';

// RFC-compliant CSV Parser
function parseCSV(text: string) {
  const lines: string[][] = [];
  let row: string[] = [''];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        row[row.length - 1] += '"';
        i++; // skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push('');
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i++; // skip \n
      }
      lines.push(row);
      row = [''];
    } else {
      row[row.length - 1] += char;
    }
  }
  if (row.length > 1 || row[0] !== '') {
    lines.push(row);
  }
  return lines;
}

function processCsvData(csvLines: string[][]) {
  if (csvLines.length < 2) return [];
  const headers = csvLines[0].map(h => h.trim().toLowerCase());

  // Find column indices
  const cgIdIdx = headers.findIndex(h => h.includes('employee id') || h === 'cgid' || h === 'employeeid');
  const nameIdx = headers.findIndex(h => h.includes('full name') || h === 'name' || h === 'fullname');
  const deptIdx = headers.findIndex(h => h === 'department');
  const emailIdx = headers.findIndex(h => h.includes('email') || h === 'email' || h.includes('email id'));
  const mobileIdx = headers.findIndex(h => h.includes('mobile') || h.includes('phone') || h === 'phonenumber' || h.includes('mobile number'));

  const employees = [];
  for (let i = 1; i < csvLines.length; i++) {
    const row = csvLines[i];
    if (row.length < 2 || !row[emailIdx]) continue;

    employees.push({
      cgId: cgIdIdx !== -1 ? row[cgIdIdx]?.trim() || null : null,
      name: nameIdx !== -1 ? row[nameIdx]?.trim() || '' : '',
      department: deptIdx !== -1 ? row[deptIdx]?.trim() || null : null,
      email: emailIdx !== -1 ? row[emailIdx]?.trim() || '' : '',
      phoneNumber: mobileIdx !== -1 ? row[mobileIdx]?.trim() || null : null,
      darwinId: null,
    });
  }
  return employees;
}

export function AdminDashboard({ showFuturePotentialByEc = true }: { showFuturePotentialByEc?: boolean } = {}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const role = localStorage.getItem('userRole');
  const isAdmin = role === 'ADMIN' || role === 'APPROVAL_TEAM';

  // Import modal states
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  // Cascading Filter States (session-cached)
  const [selectedCommitteeNames, setSelectedCommitteeNames] = useSessionState<string[]>('admin_selectedCommitteeNames', []);
  const [selectedLeaderIds, setSelectedLeaderIds] = useSessionState<string[]>('admin_selectedLeaderIds', []);
  const [selectedHeadIds, setSelectedHeadIds] = useSessionState<string[]>('admin_selectedHeadIds', []);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useSessionState<string[]>('admin_selectedAssigneeIds', []);
  const [isCommitteeDropdownOpen, setIsCommitteeDropdownOpen] = useState(false);
  const [isLeaderDropdownOpen, setIsLeaderDropdownOpen] = useState(false);
  const [isHeadDropdownOpen, setIsHeadDropdownOpen] = useState(false);
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);

  // Search States
  const [committeeSearch, setCommitteeSearch] = useState('');
  const [leaderSearch, setLeaderSearch] = useState('');
  const [headSearch, setHeadSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const committeeDropdownRef = useRef<HTMLDivElement>(null);
  const leaderDropdownRef = useRef<HTMLDivElement>(null);
  const headDropdownRef = useRef<HTMLDivElement>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (committeeDropdownRef.current && !committeeDropdownRef.current.contains(event.target as Node)) {
        setIsCommitteeDropdownOpen(false);
      }
      if (leaderDropdownRef.current && !leaderDropdownRef.current.contains(event.target as Node)) {
        setIsLeaderDropdownOpen(false);
      }
      if (headDropdownRef.current && !headDropdownRef.current.contains(event.target as Node)) {
        setIsHeadDropdownOpen(false);
      }
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: userRoleMappings } = useQuery({
    queryKey: ['userRoleMappings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/user-role-mappings');
      return res.data.data || [];
    }
  });

  const { data: departmentGroupsData } = useQuery({
    queryKey: ['department-groups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/department-groups');
      return res.data.data || [];
    }
  });

  const allCommittees = Array.from(new Set<string>(
    (departmentGroupsData || []).map((g: any) => g.name)
  )).sort();

  const filteredCommittees = allCommittees.filter((c: any) => typeof c === 'string' && c.toLowerCase().includes(committeeSearch.toLowerCase()));

  const allLeaders = userRoleMappings?.filter((u: any) => u.role === 'LEADER') || [];
  const filteredLeaders = selectedCommitteeNames.length > 0
    ? allLeaders.filter((l: any) => l.managedDepartments?.some((dept: string) => selectedCommitteeNames.includes(dept)))
    : allLeaders;

  const searchedLeaders = filteredLeaders.filter((l: any) => l.name.toLowerCase().includes(leaderSearch.toLowerCase()));

  const selectedLeaders = filteredLeaders.filter((l: any) => selectedLeaderIds.includes(l.id));

  // --- Dropdown lists only (Heads / Members) ---
  let activeDepartmentsForHeads: string[] =
    selectedLeaders.length > 0
      ? Array.from(new Set(selectedLeaders.flatMap((l: any) => l.managedDepartments || [])))
      : [...selectedCommitteeNames];
  if (selectedCommitteeNames.length > 0 && selectedLeaders.length > 0) {
    activeDepartmentsForHeads = activeDepartmentsForHeads.filter((d: string) =>
      selectedCommitteeNames.includes(d)
    );
  }
  const hasActiveScope = activeDepartmentsForHeads.length > 0;

  const committeeHeads = userRoleMappings?.filter((u: any) =>
    u.role === 'COMMITTEE_HEAD' &&
    (!hasActiveScope || u.managedDepartments?.some((dept: string) => activeDepartmentsForHeads.includes(dept)))
  ) || [];

  const searchedHeads = committeeHeads.filter((h: any) => h.name.toLowerCase().includes(headSearch.toLowerCase()));
  const selectedHeadsList = (userRoleMappings || []).filter(
    (u: any) => u.role === 'COMMITTEE_HEAD' && selectedHeadIds.includes(u.id)
  );

  const allCommitteeMembers = userRoleMappings?.filter((u: any) =>
    u.role === 'COMMITTEE_MEMBER' &&
    (!hasActiveScope || u.managedDepartments?.some((dept: string) => activeDepartmentsForHeads.includes(dept)))
  ) || [];

  // Member dropdown = committee mapping (managedDepartments), NOT reportsTo.
  // Head selected → members who share that head's mapped committee(s).
  const filteredMembers =
    selectedHeadIds.length > 0
      ? (() => {
          const headMappedDepts = new Set(
            selectedHeadsList.flatMap((h: any) => h.managedDepartments || [])
          );
          const pool = hasActiveScope
            ? allCommitteeMembers
            : (userRoleMappings || []).filter((u: any) => u.role === 'COMMITTEE_MEMBER');
          return pool.filter((m: any) =>
            (m.managedDepartments || []).some((d: string) => headMappedDepts.has(d))
          );
        })()
      : allCommitteeMembers;

  const searchedMembers = filteredMembers.filter((m: any) => m.name.toLowerCase().includes(memberSearch.toLowerCase()));

  // --- Idea / stats filter (existing priority) ---
  // Committee names (Finance) must expand to underlying depts so the ideas table can match idea.department
  const toIdeaDepartments = (names: string[]) => {
    const out = new Set<string>();
    for (const name of names) {
      const group = (departmentGroupsData || []).find((g: any) => g.name === name);
      if (group?.departments?.length) {
        group.departments.forEach((d: string) => out.add(d));
      } else {
        out.add(name);
      }
    }
    return Array.from(out);
  };

  let effectiveDepartmentIds: string | undefined = undefined;
  let finalAssigneeIds: string[] = [];

  if (selectedAssigneeIds.length > 0) {
    finalAssigneeIds = selectedAssigneeIds;
  } else if (selectedHeadIds.length > 0) {
    let headDepts = Array.from(new Set(selectedHeadsList.flatMap((h: any) => h.managedDepartments || []))) as string[];
    if (selectedCommitteeNames.length > 0) {
      headDepts = headDepts.filter((d: string) => selectedCommitteeNames.includes(d));
    }
    if (headDepts.length > 0) {
      effectiveDepartmentIds = toIdeaDepartments(headDepts).join('|');
    } else {
      finalAssigneeIds = filteredMembers.map((m: any) => m.id);
    }
  } else if (selectedLeaderIds.length > 0) {
    let leaderDepts = Array.from(
      new Set(
        (userRoleMappings || [])
          .filter((u: any) => u.role === 'LEADER' && selectedLeaderIds.includes(u.id))
          .flatMap((l: any) => l.managedDepartments || [])
      )
    ) as string[];
    if (selectedCommitteeNames.length > 0) {
      leaderDepts = leaderDepts.filter((d: string) => selectedCommitteeNames.includes(d));
    }
    if (leaderDepts.length > 0) {
      effectiveDepartmentIds = toIdeaDepartments(leaderDepts).join('|');
    } else {
      finalAssigneeIds = filteredMembers.map((m: any) => m.id);
    }
  } else if (selectedCommitteeNames.length > 0) {
    effectiveDepartmentIds = toIdeaDepartments(selectedCommitteeNames).join('|');
  }

  // 1. Fetch Admin KPIs stats
  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats', finalAssigneeIds.join(','), effectiveDepartmentIds],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats', {
        params: {
          assigneeId: finalAssigneeIds.length > 0 ? finalAssigneeIds.join(',') : undefined,
          department: effectiveDepartmentIds || undefined
        }
      });
      return res.data.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`/admin/ideas/${id}`, { data: { reason } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminIdeas'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    }
  });

  const handleDelete = (id: string, reason: string) => {
    deleteMutation.mutate({ id, reason });
  };

  const handleBulkDelete = async (ids: string[], reason: string) => {
    await Promise.all(ids.map(id => deleteMutation.mutateAsync({ id, reason })));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile(file);
  };

  const uploadFile = (file: File) => {
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCsv = file.name.endsWith('.csv');

    if (!isExcel && !isCsv) {
      setImportError('Please select a valid CSV or Excel (.xlsx, .xls) file.');
      return;
    }

    setImporting(true);
    setImportError('');
    setImportSuccess('');

    const reader = new FileReader();

    if (isExcel) {
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

          const parsedEmployees = processCsvData(json);

          if (parsedEmployees.length === 0) {
            throw new Error('No valid employee records found in the Excel file.');
          }

          const res = await apiClient.post('/admin/employees/import', { employees: parsedEmployees });
          setImportSuccess(res.data.message || `Successfully imported ${parsedEmployees.length} employees.`);
          if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err: any) {
          setImportError(err.response?.data?.error?.message || err.message || 'Failed to import employees.');
        } finally {
          setImporting(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = async (event) => {
        try {
          const text = event.target?.result as string;
          const lines = parseCSV(text);
          const parsedEmployees = processCsvData(lines);

          if (parsedEmployees.length === 0) {
            throw new Error('No valid employee records found in the CSV file.');
          }

          const res = await apiClient.post('/admin/employees/import', { employees: parsedEmployees });
          setImportSuccess(res.data.message || `Successfully imported ${parsedEmployees.length} employees.`);
          if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err: any) {
          setImportError(err.response?.data?.error?.message || err.message || 'Failed to import employees.');
        } finally {
          setImporting(false);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Layout title="Admin Portal">
      <div className="flex flex-col sm:justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800">DOM680 Dashboard</h2>
            <p className="text-slate-500 text-xs mt-0.5">Review, prioritize and moderate idea workflow</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {isAdmin && (
              <>
                <Link
                  to="/admin/deleted-ideas"
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-3 sm:px-3.5 py-1.5 sm:py-1.5 rounded-lg text-[11px] sm:text-xs transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm border border-slate-200"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Deleted Log</span>
                </Link>
                <button
                  onClick={() => setIsImportModalOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 sm:px-3.5 py-1.5 sm:py-1.5 rounded-lg text-[11px] sm:text-xs transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm shadow-emerald-600/10 border-0"
                >
                  <PlusSquare className="w-3.5 h-3.5" />
                  <span>Import Employee List</span>
                </button>
                <button
                  onClick={() => navigate('/admin/bulk-evaluate-import')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 sm:px-3.5 py-1.5 sm:py-1.5 rounded-lg text-[11px] sm:text-xs transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm border-0"
                >
                  <FileCheck className="w-3.5 h-3.5" />
                  <span>Bulk Evaluation Import</span>
                </button>

              </>
            )}
          </div>
        </div>

        {/* Cascading Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row flex-wrap gap-3 sm:gap-4 mt-2 mb-2">
          {/* Committee Dropdown */}
          <div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={committeeDropdownRef}>
            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Building className="w-3 h-3 text-purple-500" />
              Committee
            </label>
            <div className={`relative ${isCommitteeDropdownOpen ? 'z-50' : 'z-20'}`}>
              <Filter className="w-4 h-4 absolute left-2.5 top-1.5 text-slate-400 pointer-events-none" />
              <button
                onClick={() => setIsCommitteeDropdownOpen(!isCommitteeDropdownOpen)}
                className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors focus:bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-left flex justify-between items-center shadow-sm"
              >
                <span className="truncate font-medium text-slate-700">
                  {selectedCommitteeNames.length === 0
                    ? 'All Committees'
                    : selectedCommitteeNames.length === 1
                      ? selectedCommitteeNames[0]
                      : `${selectedCommitteeNames.length} Selected`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
              </button>

              {isCommitteeDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                  <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search committees..."
                        className="w-full text-xs pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                        value={committeeSearch}
                        onChange={(e) => setCommitteeSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-60 flex-1">
                    <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        checked={selectedCommitteeNames.length === allCommittees.length && allCommittees.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCommitteeNames(allCommittees);
                          } else {
                            setSelectedCommitteeNames([]);
                          }
                          setSelectedLeaderIds([]);
                          setSelectedHeadIds([]);
                          setSelectedAssigneeIds([]);
                        }}
                      />
                      <span className="text-sm text-slate-800 font-bold">Select All</span>
                    </label>
                    {filteredCommittees?.map((name: any) => (
                      <label key={name} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                          checked={selectedCommitteeNames.includes(name)}
                          onChange={(e) => {
                            setSelectedCommitteeNames(prev =>
                              e.target.checked ? [...prev, name] : prev.filter(n => n !== name)
                            );
                            setSelectedLeaderIds([]);
                            setSelectedHeadIds([]);
                            setSelectedAssigneeIds([]);
                          }}
                        />
                        <span className="text-sm text-slate-700 leading-tight group-hover:text-purple-700 transition-colors truncate">{name}</span>
                      </label>
                    ))}
                    {filteredCommittees.length === 0 && (
                      <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">No committees found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Leader Dropdown */}
          <div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={leaderDropdownRef}>
            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Users className="w-3 h-3 text-blue-500" />
              Committee Leader
            </label>
            <div className={`relative ${isLeaderDropdownOpen ? 'z-50' : 'z-20'}`}>
              <Filter className="w-4 h-4 absolute left-2.5 top-1.5 text-slate-400 pointer-events-none" />
              <button
                onClick={() => setIsLeaderDropdownOpen(!isLeaderDropdownOpen)}
                className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex justify-between items-center shadow-sm"
              >
                <span className="truncate font-medium text-slate-700">
                  {selectedLeaderIds.length === 0
                    ? 'All Leaders'
                    : selectedLeaderIds.length === 1
                      ? filteredLeaders?.find((m: any) => m.id === selectedLeaderIds[0])?.name || 'Selected'
                      : `${selectedLeaderIds.length} Selected`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
              </button>

              {isLeaderDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                  <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search leaders..."
                        className="w-full text-xs pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                        value={leaderSearch}
                        onChange={(e) => setLeaderSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-60 flex-1">
                    <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedLeaderIds.length === filteredLeaders.length && filteredLeaders.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeaderIds(filteredLeaders.map((l: any) => l.id));
                          } else {
                            setSelectedLeaderIds([]);
                          }
                          setSelectedHeadIds([]);
                          setSelectedAssigneeIds([]);
                        }}
                      />
                      <span className="text-sm text-slate-800 font-bold">Select All</span>
                    </label>
                    {searchedLeaders?.map((leader: any) => (
                      <label key={leader.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0"
                          checked={selectedLeaderIds.includes(leader.id)}
                          onChange={(e) => {
                            setSelectedLeaderIds(prev =>
                              e.target.checked ? [...prev, leader.id] : prev.filter(id => id !== leader.id)
                            );
                            setSelectedHeadIds([]);
                            setSelectedAssigneeIds([]);
                          }}
                        />
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate leading-tight text-slate-700 font-medium group-hover:text-blue-700 transition-colors">{leader.name}</span>
                          {(() => {
                            const allDepts = leader.managedDepartments || [];
                            const displayDepts = selectedCommitteeNames.length > 0
                              ? allDepts.filter((d: string) => selectedCommitteeNames.includes(d))
                              : allDepts;
                            return displayDepts.length > 0 ? (
                              <span className="text-slate-500 text-[10px] uppercase font-bold leading-tight truncate mt-0.5">{displayDepts.join(' | ')}</span>
                            ) : null;
                          })()}
                        </div>
                      </label>
                    ))}
                    {searchedLeaders.length === 0 && (
                      <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">No leaders found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Head Dropdown */}
          <div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={headDropdownRef}>
            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Users className="w-3 h-3 text-emerald-500" />
              Committee Head
            </label>
            <div className={`relative ${isHeadDropdownOpen ? 'z-50' : 'z-20'}`}>
              <Filter className="w-4 h-4 absolute left-2.5 top-1.5 text-slate-400 pointer-events-none" />
              <button
                onClick={() => setIsHeadDropdownOpen(!isHeadDropdownOpen)}
                className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-left flex justify-between items-center shadow-sm"
              >
                <span className="truncate font-medium text-slate-700">
                  {selectedHeadIds.length === 0
                    ? 'All Heads'
                    : selectedHeadIds.length === 1
                      ? committeeHeads?.find((m: any) => m.id === selectedHeadIds[0])?.name || 'Selected'
                      : `${selectedHeadIds.length} Selected`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
              </button>

              {isHeadDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                  <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search heads..."
                        className="w-full text-xs pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                        value={headSearch}
                        onChange={(e) => setHeadSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-60 flex-1">
                    <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        checked={selectedHeadIds.length === committeeHeads.length && committeeHeads.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedHeadIds(committeeHeads.map((h: any) => h.id));
                          } else {
                            setSelectedHeadIds([]);
                          }
                          setSelectedAssigneeIds([]);
                        }}
                      />
                      <span className="text-sm text-slate-800 font-bold">Select All</span>
                    </label>
                    {searchedHeads?.map((head: any) => (
                      <label key={head.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 shrink-0"
                          checked={selectedHeadIds.includes(head.id)}
                          onChange={(e) => {
                            setSelectedHeadIds(prev =>
                              e.target.checked ? [...prev, head.id] : prev.filter(id => id !== head.id)
                            );
                            setSelectedAssigneeIds([]);
                          }}
                        />
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate leading-tight text-slate-700 font-medium group-hover:text-emerald-700 transition-colors">{head.name}</span>
                          {(() => {
                            const allDepts = head.managedDepartments || [];
                            const activeDepts = selectedCommitteeNames.length > 0
                              ? allDepts.filter((d: string) => selectedCommitteeNames.includes(d))
                              : selectedLeaderIds.length > 0
                                ? allDepts.filter((d: string) => activeDepartmentsForHeads.includes(d))
                                : allDepts;
                            return activeDepts.length > 0 ? (
                              <span className="text-slate-500 text-[10px] uppercase font-bold leading-tight truncate mt-0.5">{activeDepts.join(' | ')}</span>
                            ) : null;
                          })()}
                        </div>
                      </label>
                    ))}
                    {searchedHeads.length === 0 && (
                      <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">No heads found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Member Dropdown */}
          <div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={memberDropdownRef}>
            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Users className="w-3 h-3 text-indigo-500" />
              Committee Member
            </label>
            <div className={`relative ${isMemberDropdownOpen ? 'z-50' : 'z-20'}`}>
              <Filter className="w-4 h-4 absolute left-2.5 top-1.5 text-slate-400 pointer-events-none" />
              <button
                onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}
                className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-left flex justify-between items-center shadow-sm"
              >
                <span className="truncate font-medium text-slate-700">
                  {selectedAssigneeIds.length === 0
                    ? 'All Members'
                    : selectedAssigneeIds.length === 1
                      ? filteredMembers?.find((m: any) => m.id === selectedAssigneeIds[0])?.name || 'Selected'
                      : `${selectedAssigneeIds.length} Selected`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
              </button>

              {isMemberDropdownOpen && (
                <div className="absolute top-full right-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                  <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search members..."
                        className="w-full text-xs pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-60 flex-1">
                    <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={selectedAssigneeIds.length === filteredMembers.length && filteredMembers.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAssigneeIds(filteredMembers.map((m: any) => m.id));
                          } else {
                            setSelectedAssigneeIds([]);
                          }
                        }}
                      />
                      <span className="text-sm text-slate-800 font-bold">Select All</span>
                    </label>
                    {searchedMembers?.map((member: any) => (
                      <label key={member.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                          checked={selectedAssigneeIds.includes(member.id)}
                          onChange={(e) => {
                            setSelectedAssigneeIds(prev =>
                              e.target.checked ? [...prev, member.id] : prev.filter(id => id !== member.id)
                            );
                          }}
                        />
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate leading-tight text-slate-700 font-medium group-hover:text-indigo-700 transition-colors">{member.name}</span>
                          {(() => {
                            let depts = member.managedDepartments?.length > 0
                              ? member.managedDepartments
                              : (() => {
                                const parent = userRoleMappings?.find((u: any) => u.id === member.reportsToId);
                                return parent?.managedDepartments || [];
                              })();
                            // Intersect with active committee filter
                            if (selectedCommitteeNames.length > 0) {
                              depts = depts.filter((d: string) => selectedCommitteeNames.includes(d));
                            } else if (selectedLeaderIds.length > 0) {
                              depts = depts.filter((d: string) => activeDepartmentsForHeads.includes(d));
                            }
                            return depts?.length > 0 ? (
                              <span className="text-slate-500 text-[10px] uppercase font-bold leading-tight truncate mt-0.5">{depts.join(' | ')}</span>
                            ) : null;
                          })()}
                        </div>
                      </label>
                    ))}
                    {searchedMembers.length === 0 && (
                      <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">No members found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selected Filters Summary Tags */}
        {(selectedCommitteeNames.length > 0 || selectedLeaderIds.length > 0 || selectedHeadIds.length > 0 || selectedAssigneeIds.length > 0) && (
          <div className="mb-6 flex flex-col gap-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Active Filters:</span>
            <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200/80 shadow-sm">

              {/* Committees */}
              {selectedCommitteeNames.map((name: string) => (
                <div key={`committee-${name}`} className="bg-purple-100 text-purple-800 text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm border border-purple-200/50">
                  <span className="font-medium opacity-75 mr-0.5">Committee:</span>
                  <span className="font-semibold">{name}</span>
                  <button onClick={() => setSelectedCommitteeNames(prev => prev.filter(i => i !== name))} className="ml-0.5 text-purple-400 hover:text-purple-700 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {/* Leaders */}
              {selectedLeaderIds.map((id: string) => {
                const leader = allLeaders?.find((l: any) => l.id === id);
                if (!leader) return null;
                return (
                  <div key={`leader-${id}`} className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm border border-blue-200/50">
                    <span className="font-medium opacity-75 mr-0.5">Leader:</span>
                    <span className="font-semibold">{leader.name}</span>
                    {leader.managedDepartments?.length > 0 && (
                      <span className="text-blue-600/80 border-l border-blue-300/50 pl-1.5 font-medium">{leader.managedDepartments.join(' | ')}</span>
                    )}
                    <button onClick={() => setSelectedLeaderIds(prev => prev.filter(i => i !== id))} className="ml-0.5 text-blue-400 hover:text-blue-700 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}

              {/* Heads */}
              {selectedHeadIds.map((id: string) => {
                const head = committeeHeads?.find((h: any) => h.id === id);
                if (!head) return null;
                return (
                  <div key={`head-${id}`} className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm border border-emerald-200/50">
                    <span className="font-medium opacity-75 mr-0.5">Head:</span>
                    <span className="font-semibold">{head.name}</span>
                    {head.managedDepartments?.length > 0 && (
                      <span className="text-emerald-600/80 border-l border-emerald-300/50 pl-1.5 font-medium">{head.managedDepartments.join(' | ')}</span>
                    )}
                    <button onClick={() => setSelectedHeadIds(prev => prev.filter(i => i !== id))} className="ml-0.5 text-emerald-400 hover:text-emerald-700 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}

              {/* Members */}
              {selectedAssigneeIds.map((id: string) => {
                const member = filteredMembers?.find((m: any) => m.id === id);
                if (!member) return null;
                const depts = member.managedDepartments?.length > 0 ? member.managedDepartments : (() => {
                  const parent = userRoleMappings?.find((u: any) => u.id === member.reportsToId);
                  return parent?.managedDepartments || [];
                })();
                return (
                  <div key={`member-${id}`} className="bg-indigo-100 text-indigo-800 text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm border border-indigo-200/50">
                    <span className="font-medium opacity-75 mr-0.5">Member:</span>
                    <span className="font-semibold">{member.name}</span>
                    {depts?.length > 0 && (
                      <span className="text-indigo-600/80 border-l border-indigo-300/50 pl-1.5 font-medium">{depts.join(' | ')}</span>
                    )}
                    <button onClick={() => setSelectedAssigneeIds(prev => prev.filter(i => i !== id))} className="ml-0.5 text-indigo-400 hover:text-indigo-700 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}

              <button
                onClick={() => { setSelectedCommitteeNames([]); setSelectedLeaderIds([]); setSelectedHeadIds([]); setSelectedAssigneeIds([]); }}
                className="text-xs font-medium text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:border-slate-300 px-2.5 py-1.5 rounded-lg shadow-sm transition-all ml-1"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Metrics overview */}
      {statsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="mb-8">

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
            {/* 1) Total Ideas */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600 shrink-0">
                  <Folder className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Total Ideas</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.totalIdeas || 0}</p>
            </div>

            {/* 2) Hold */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                  <ShieldAlert className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Hold</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.infoRequested || 0}</p>
            </div>

            {/* 3) Duplicate */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-slate-100 p-1.5 rounded-lg text-slate-600 shrink-0">
                  <Copy className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Duplicate</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.duplicateCount || 0}</p>
            </div>

            {/* 4) Rejected */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-red-50 p-1.5 rounded-lg text-red-600 shrink-0">
                  <Ban className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Rejected</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.rejected || 0}</p>
            </div>

            {/* 5) Reviewed */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-orange-50 p-1.5 rounded-lg text-orange-600 shrink-0">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Reviewed</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.underReview || 0}</p>
            </div>


            {/* 6) Yet to Be Reviewed */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                  <Send className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Yet to Be Reviewed</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.submitted || 0}</p>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-emerald-50 p-1 rounded-lg text-emerald-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Approved by MIS">Approved by MIS</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.misApprovedCount || 0}</p>
                </div>
                <CountCardHint text="Ideas the MIS team has approved. The idea status stays the same." />
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-rose-50 p-1 rounded-lg text-rose-600 shrink-0">
                  <Ban className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Rejected by MIS">Rejected by MIS</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.misRejectedCount || 0}</p>
                </div>
                <CountCardHint text="Ideas the MIS team has rejected. A rejected idea is stored as a duplicate." />
              </div>
            </div>


            {/* 7) WIP – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-cyan-50 p-1 rounded-lg text-cyan-600 shrink-0">
                  <PlayCircle className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">WIP</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.workInProgressCount || 0}</p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.wipTotalAmount || 0} textSize="text-[15px]" />
                </div>
              </div>
            </div>

            {/* 8) Pending IC Head Approval – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-emerald-50 p-1 rounded-lg text-emerald-600 shrink-0">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Pending IC Head Approval</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.pendingHeadApproval || 0}</p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.pendingHeadApprovalValue || 0} textSize="text-[15px]" />
                </div>
              </div>
            </div>

            {/* 9) Requested EC Head Approval – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-fuchsia-50 p-1 rounded-lg text-fuchsia-600 shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Requested EC Head Approval</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.pendingExternalApprovalValue || 0} textSize="text-[15px]" />
                </div>
              </div>
            </div>

            {/* 10) EC Head Approved – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-green-50 p-1 rounded-lg text-green-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Approved</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.approved || 0}</p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.actualExternalApprovedAmount || 0} textSize="text-[15px]" />
                </div>
              </div>
            </div>

            {showFuturePotentialByEc && (
            /* 10b) Future potential by EC = Requested remaining + Approved/Email Confirmed shortfall */
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-amber-50 p-1 rounded-lg text-amber-600 shrink-0">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Future potential by EC</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">
                    {/* OLD: Requested − (Approved + Email Confirmed)
                    {(globalStats?.kpis?.pendingExternalApproval || 0) -
                      ((globalStats?.kpis?.approved || 0) + (globalStats?.kpis?.emailConfirmedCount || 0))}
                    */}
                    {globalStats?.kpis?.futurePotentialByEcCount || 0}
                  </p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay
                    amount={
                      /* OLD: Requested − (Approved + Email Confirmed)
                      (globalStats?.kpis?.pendingExternalApprovalValue || 0) -
                      ((globalStats?.kpis?.actualExternalApprovedAmount || 0) +
                        (globalStats?.kpis?.emailConfirmationAnnualSavings || 0))
                      */
                      globalStats?.kpis?.futurePotentialByEcValue || 0
                    }
                    textSize="text-[15px]"
                    forceCrores={true}
                  />
                </div>
              </div>
            </div>
            )}

            {/* 11) EC Head Rejected – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-rose-50 p-1 rounded-lg text-rose-600 shrink-0">
                  <XCircle className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Rejected</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.externalRejected || 0}</p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.externalRejectedValue || 0} textSize="text-[15px]" />
                </div>
              </div>
            </div>

            {/* 12) EC Head Email Confirmed – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-indigo-50 p-1 rounded-lg text-indigo-600 shrink-0">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Email Confirmed</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.emailConfirmedCount || 0}</p>
                </div>
                <div className="text-right flex-1 min-w-0 pl-1">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.emailConfirmationAnnualSavings || 0} textSize="text-[15px]" />
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      <div className="mt-8">
        <IdeasFilterTable
          apiEndpoint="/admin/ideas"
          queryKeyPrefix="adminIdeas"
          showExport={true}
          showFuturePotentialByEc={showFuturePotentialByEc}
          allowDelete={isAdmin}
          assigneeId={finalAssigneeIds.length > 0 ? finalAssigneeIds.join(',') : undefined}
          baseDepartment={effectiveDepartmentIds || undefined}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          actionButtonLabel="Evaluate"
          includeMisStatuses
        />
      </div>

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-800">Import Employee List</h3>
              <button
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportError('');
                  setImportSuccess('');
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                Upload a CSV or Excel file containing the employee list. The file must include columns for <span className="font-semibold text-slate-800">Email</span>, <span className="font-semibold text-slate-800">Full Name</span>, and optionally <span className="font-semibold text-slate-800">Department</span>, <span className="font-semibold text-slate-800">Employee ID (CGID)</span>, and <span className="font-semibold text-slate-800">Mobile</span>.
              </p>

              {importError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start space-x-2 border border-red-100">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{importError}</span>
                </div>
              )}

              {importSuccess && (
                <div className="p-3 bg-emerald-50 text-emerald-700 rounded-lg text-sm flex items-start space-x-2 border border-emerald-100">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{importSuccess}</span>
                </div>
              )}

              <div className="mt-4">
                <input
                  type="file"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="w-full bg-blue-50 border-2 border-dashed border-blue-200 hover:border-blue-300 hover:bg-blue-100/50 text-blue-700 font-medium py-8 rounded-xl flex flex-col items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                      <span>Processing file...</span>
                    </>
                  ) : (
                    <>
                      <PlusSquare className="w-8 h-8 mb-3 text-blue-500" />
                      <span>Click to browse for file</span>
                      <span className="text-xs text-blue-400 mt-1">.csv, .xls, .xlsx</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportError('');
                  setImportSuccess('');
                }}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


    </Layout>
  );
}
